import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, LinearProgress, MenuItem,
  Paper, Stack, TextField, Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';

import AppDialog from '../../../shared/components/AppDialog';
import LabeledField from '../../../shared/components/LabeledField';
import ProcessingCloseWarningDialog from '../../../shared/components/ProcessingCloseWarningDialog';
import { translateMessage } from '../../../shared/api';
import useProcessingCloseGuard from '../../../shared/hooks/useProcessingCloseGuard';
import { formatHistoryDateTime } from '../../../shared/utils/historyDateTime';
import { formatEf } from '../../../shared/utils/number';
import {
  isTerminalSyncStatus,
} from '../model/emissionFactorSyncRecovery';
import {
  latestSourceHistory,
} from '../model/emissionFactorSyncHistoryModel';
import { syncStatusTone } from '../model/emissionFactorSyncPresentation';
import { EF_SYNC_SOURCE_NOT_AVAILABLE } from '../../../shared/constants';

export const statusLabel = (t, status) => t(
  `emissionFactor.sync.statuses.${status}`,
  { defaultValue: t('emissionFactor.sync.statuses.UNKNOWN') },
);

export const errorLabel = (message, params = {}) => (
  translateMessage(message, 500, params)
);

export const warningLabel = (message, params = {}) => (
  translateMessage(message, 400, params)
);

export const totalChangedCount = (result) => (result?.sources || []).reduce(
  (total, source) => total + Number(source.changed_count || 0),
  0,
);

export const isSourceNotAvailableResult = (result) => (
  result?.status === 'FAILED'
  && (result.sources || []).length > 0
  && (result.sources || []).every((source) => (
    (source.errors || []).some(
      (error) => error.message === EF_SYNC_SOURCE_NOT_AVAILABLE,
    )
  ))
);

export const syncOutcomeMessageKey = (result) => {
  const status = result?.status;
  if (status === 'SUCCESS') {
    return totalChangedCount(result) > 0
      ? 'emissionFactor.sync.message.withChanges'
      : 'emissionFactor.sync.message.noChanges';
  }
  if (status === 'PARTIAL') return 'emissionFactor.sync.message.partial';
  if (status === 'FAILED') {
    return isSourceNotAvailableResult(result)
      ? null
      : 'emissionFactor.sync.message.failed';
  }
  return null;
};

export const sourceLabel = (t, sourceType) => t(
  `emissionFactor.sync.sources.${sourceType}`,
  { defaultValue: t('emissionFactor.sync.sources.UNKNOWN') },
);

export const activeSyncScopeKey = (sourceTypes) => {
  const requested = new Set(sourceTypes || []);
  if (requested.has('DB_V3') && requested.has('DENKI')) return 'ALL';
  if (requested.has('DENKI')) return 'SCOPE_2';
  if (requested.has('DB_V3')) return 'SCOPE_13';
  return 'ALL';
};

export function OperationOutcome({ result }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const completeness = result?.global_completeness;
  const sourceNotAvailable = isSourceNotAvailableResult(result);
  const messageKey = syncOutcomeMessageKey(result);
  if (!completeness && !messageKey && !sourceNotAvailable) return null;
  if (sourceNotAvailable) {
    return (
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        {t('emissionFactor.sync.message.sourceNotAvailable')}
      </Alert>
    );
  }
  return (
    <Stack spacing={1} sx={{ mb: 1.5 }}>
      {completeness && (
        <Alert severity={completeness.is_complete ? 'success' : 'warning'}>
          {t(`emissionFactor.sync.completeness.${completeness.reason}`, {
            expected: completeness.expected_count,
            present: completeness.present_count,
            missing: completeness.missing_count,
            defaultValue: t('emissionFactor.sync.completeness.UNKNOWN'),
          })}
        </Alert>
      )}
      {messageKey && (
        <Alert
          severity={result.status === 'FAILED' ? 'error' : 'info'}
          action={(
            <Button color="inherit" size="small" onClick={() => navigate('/data-input')}>
              {t('emissionFactor.sync.goToDataInput')}
            </Button>
          )}
        >
          {t(messageKey)}
          {result.status === 'FAILED' && result.error_summary?.message && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {errorLabel(result.error_summary.message, result.error_summary.params)}
            </Typography>
          )}
        </Alert>
      )}
    </Stack>
  );
}

export function SourceResult({ source }) {
  const { t } = useTranslation();
  const [showChanges, setShowChanges] = useState(false);
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Box>
          <Typography variant="subtitle2" fontWeight={800}>{sourceLabel(t, source.source_type)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {source.version_label || '—'} · {t('emissionFactor.sync.foundPublished', {
              found: source.found_count,
              expected: source.expected_count,
              published: source.published_count,
            })}
            {source.application_mode && ` · ${t(
              `emissionFactor.sync.applicationModes.${source.application_mode}`,
              { defaultValue: t('emissionFactor.sync.applicationModes.UNKNOWN') },
            )}`}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={statusLabel(t, source.status)}
          color={syncStatusTone(source.status)}
          variant="outlined"
        />
      </Stack>
      {(source.errors || []).map((error, index) => (
        <Alert
          key={`${source.source_type}:${error.message || error.code || index}:${index}`}
          severity="error"
          sx={{ mt: 1 }}
        >
          {errorLabel(error.message || error.code, error.params)}
        </Alert>
      ))}
      {(source.warnings || []).map((warning, index) => (
        <Alert
          key={`${source.source_type}:${warning.message || warning.code || index}:${index}`}
          severity="warning"
          sx={{ mt: 1 }}
        >
          {warningLabel(warning.message || warning.code, warning.params)}
        </Alert>
      ))}
      {(source.unresolved_items || []).map((item) => (
        <Alert key={`${source.source_type}:${item.item_key}`} severity="warning" sx={{ mt: 1 }}>
          {t('emissionFactor.sync.unresolvedItem', {
            itemKey: item.item_key,
            reason: t(`emissionFactor.sync.unresolvedReasons.${item.reason_code}`, {
              defaultValue: item.reason_code,
            }),
            attempts: item.attempt_count,
          })}
        </Alert>
      ))}
      {source.changes?.length > 0 && (
        <>
          <Button size="small" sx={{ mt: 0.5 }} onClick={() => setShowChanges((value) => !value)}>
            {t(
              showChanges ? 'emissionFactor.sync.hideChanges' : 'emissionFactor.sync.showChanges',
              { count: source.changed_count },
            )}
          </Button>
          <Collapse in={showChanges}>
            <Stack spacing={0.75} sx={{ mt: 0.5, maxHeight: 190, overflowY: 'auto' }}>
              {source.changes.map((change) => (
                <Box key={change.item_key} sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1, py: 0.75 }}>
                  <Typography variant="caption" fontWeight={750}>{change.item_key} · {change.business_name}</Typography>
                  <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                    {formatEf(change.old_value)} → {formatEf(change.new_value)} {change.factor_unit}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Collapse>
        </>
      )}
    </Box>
  );
}

function SyncSourceCard({
  title, sourceType, latest, isDisabled, onSync,
}) {
  const { t, i18n } = useTranslation();
  const status = latest?.source?.status;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
        {status ? (
          <Chip
            size="small"
            label={statusLabel(t, status)}
            color={syncStatusTone(status)}
            variant="outlined"
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t('emissionFactor.sync.card.noSyncYet')}
          </Typography>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {t('emissionFactor.sync.card.source')}: {sourceLabel(t, sourceType)}
        {latest?.source?.version_label ? ` · ${latest.source.version_label}` : ''}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {t('emissionFactor.sync.card.lastSync')}: {latest
          ? formatHistoryDateTime(latest.timestamp, i18n.language)
          : t('emissionFactor.sync.card.never')}
      </Typography>
      <Button
        variant="contained"
        size="small"
        startIcon={isDisabled ? <CircularProgress color="inherit" size={15} /> : <SyncRoundedIcon />}
        onClick={onSync}
        disabled={isDisabled}
        sx={{ mt: 1.5 }}
      >
        {t(`emissionFactor.sync.card.sync.${sourceType}`)}
      </Button>
    </Paper>
  );
}

export default function EmissionFactorSyncDialog({
  open, policy, isProcessing, monitoringError, result, syncHistory,
  onClose, onExited, onRetryStatus, onSubmit,
}) {
  const { t } = useTranslation();
  const fiscalYearOptions = policy?.allowed_fiscal_years || [];
  const defaultFiscalYear = policy?.default_fiscal_year;
  const requiresForce = Boolean(policy?.requires_force);
  const resultIsTerminal = Boolean(result) && isTerminalSyncStatus(result.status);
  const showProcessing = isProcessing && !resultIsTerminal;
  const activeFiscalYear = showProcessing && result ? Number(result.fiscal_year) : null;
  const [fiscalYearChoice, setFiscalYearChoice] = useState(null);
  const resultFiscalYear = result ? Number(result.fiscal_year) : null;
  const choiceIsValid = fiscalYearOptions.some(
    (option) => option.fiscal_year === fiscalYearChoice,
  );
  const selectedFiscalYear = activeFiscalYear
    ?? (choiceIsValid
      ? fiscalYearChoice
      : resultFiscalYear ?? defaultFiscalYear);
  const selectedOption = fiscalYearOptions.find(
    (option) => option.fiscal_year === selectedFiscalYear,
  );
  const isNextFySelected = selectedOption?.mode === 'EARLY_AVAILABILITY_CHECK';
  const {
    requestClose,
    isWarningOpen,
    stay,
    closeAnyway,
  } = useProcessingCloseGuard({ isProcessing: showProcessing, onClose });

  const runSourceSync = (sourceTypes) => onSubmit(selectedFiscalYear, {
    force: requiresForce,
    sourceTypes,
  });

  return (
    <>
      <AppDialog
        open={open}
        onClose={requestClose}
        maxWidth="md"
        fullWidth
        TransitionProps={{ onExited }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {t(showProcessing ? 'emissionFactor.sync.processingTitle' : 'emissionFactor.sync.title')}
          <IconButton
            onClick={requestClose}
            aria-label={t('common.close')}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: { xs: 360, sm: 390 } }}>
          {!result && policy?.will_replace_manual_values === true && (
            <Alert severity="warning" sx={{ mb: 2.5 }}>
              {t('emissionFactor.sync.overwriteWarning')}
            </Alert>
          )}
          <LabeledField label={t('emissionFactor.filter.fiscalYear')} htmlFor="sync_fiscal_year" required>
            <TextField
              id="sync_fiscal_year"
              select
              size="small"
              value={selectedFiscalYear ?? ''}
              onChange={(event) => setFiscalYearChoice(Number(event.target.value))}
              disabled={Boolean(activeFiscalYear)}
              fullWidth
            >
              {fiscalYearOptions.map((option) => (
                <MenuItem key={option.fiscal_year} value={option.fiscal_year}>
                  {option.fiscal_year}
                </MenuItem>
              ))}
            </TextField>
          </LabeledField>
          {!result && isNextFySelected && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('emissionFactor.sync.nextFyInfo', { year: selectedFiscalYear })}
            </Alert>
          )}
          {showProcessing && (
            <Stack spacing={1} sx={{ mt: 2.5 }}>
              <Typography variant="body2" fontWeight={700}>
                {t(`emissionFactor.sync.history.stages.${result?.stage || 'QUEUED'}`, {
                  defaultValue: t('emissionFactor.sync.history.stages.UNKNOWN'),
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('emissionFactor.sync.progressScope', {
                  scope: t(`emissionFactor.sync.progressScopes.${activeSyncScopeKey(result?.source_types)}`),
                })}
              </Typography>
              <LinearProgress
                variant="indeterminate"
                aria-label={t('emissionFactor.sync.progress')}
              />
              {monitoringError && (
                <Alert severity="warning">{t('emissionFactor.sync.statusUnavailable')}</Alert>
              )}
            </Stack>
          )}
          {resultIsTerminal && (
            <Box sx={{ mt: 2.5 }}>
              <Divider sx={{ mb: 2 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={800}>
                  {t('emissionFactor.sync.result')} · FY{result.fiscal_year}
                </Typography>
                <Chip
                  label={statusLabel(t, result.status)}
                  color={syncStatusTone(result.status)}
                />
              </Stack>
              <OperationOutcome result={result} />
              <Stack spacing={1}>{(result.sources || []).map((source) => (
                <SourceResult key={source.source_type} source={source} />
              ))}</Stack>
            </Box>
          )}
          {!showProcessing && (
            <Stack spacing={1.5} sx={{ mt: 2.5 }}>
              <SyncSourceCard
                title={t('emissionFactor.sync.card.scope13')}
                sourceType="DB_V3"
                latest={latestSourceHistory(syncHistory, selectedFiscalYear, 'DB_V3')}
                isDisabled={Boolean(activeFiscalYear)}
                onSync={() => runSourceSync(['DB_V3'])}
              />
              <SyncSourceCard
                title={t('emissionFactor.sync.card.scope2')}
                sourceType="DENKI"
                latest={latestSourceHistory(syncHistory, selectedFiscalYear, 'DENKI')}
                isDisabled={Boolean(activeFiscalYear)}
                onSync={() => runSourceSync(['DENKI'])}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {showProcessing ? (
            <>
              {monitoringError && onRetryStatus && (
                <Button onClick={onRetryStatus}>{t('emissionFactor.sync.retryStatus')}</Button>
              )}
              <Button onClick={requestClose}>{t('common.close')}</Button>
            </>
          ) : (
            <Button onClick={requestClose}>
              {resultIsTerminal ? t('common.close') : t('common.cancel')}
            </Button>
          )}
        </DialogActions>
      </AppDialog>
      <ProcessingCloseWarningDialog
        open={isWarningOpen}
        onStay={stay}
        onCloseAnyway={closeAnyway}
      />
    </>
  );
}